import { Controller } from 'stimulus'

const P = 'lexiqamus-user-guide-page'
const sel = (name) => `.${P}-${name}`

// User guide only — screenshot lightbox (<dialog>) + in-page jump-back. Back-to-top is CSS-only (_back_to_top.html.erb).
export default class extends Controller {
  static targets = ['lightbox', 'jumpBack']

  connect () {
    this.jumpFrom = null
    this.jumpHost = null
    this.jumpTimer = null
    this.jumpTargetTimer = null

    if (this.hasLightboxTarget) {
      this.element.addEventListener('click', this.onScreenshotClick)
      this.lightboxTarget.addEventListener('click', this.onLightboxClick)
      this.lightboxTarget.addEventListener('close', this.onLightboxClosed)
    }

    if (this.hasJumpBackTarget) {
      document.addEventListener('keydown', this.onKeydown)
      document.addEventListener('click', this.onDismissJump, true)
      document.addEventListener('click', this.onAnchorClick)
      for (const ev of ['mousedown', 'wheel', 'touchstart']) {
        document.addEventListener(ev, this.onDismissJump, { passive: true, capture: true })
      }
    }
  }

  disconnect () {
    if (this.hasLightboxTarget) {
      this.element.removeEventListener('click', this.onScreenshotClick)
      this.lightboxTarget.removeEventListener('click', this.onLightboxClick)
      this.lightboxTarget.removeEventListener('close', this.onLightboxClosed)
    }

    if (this.hasJumpBackTarget) {
      document.removeEventListener('keydown', this.onKeydown)
      document.removeEventListener('click', this.onDismissJump, true)
      document.removeEventListener('click', this.onAnchorClick)
      for (const ev of ['mousedown', 'wheel', 'touchstart']) {
        document.removeEventListener(ev, this.onDismissJump, { capture: true })
      }
      this.detachJump()
    }
  }

  onScreenshotClick = (event) => {
    if (!this.hasLightboxTarget) return
    const img = event.target.closest(`${sel('screenshot')} img`)
    if (!img) return

    const map = img.closest(sel('screenshot-map'))
    let node
    if (map) {
      node = map.cloneNode(true)
      const figure = map.closest('figure')
      if (figure) node.style.setProperty('--ar', figure.style.getPropertyValue('--ar'))
    } else {
      node = document.createElement('img')
      node.src = img.src
      node.alt = img.alt
    }

    this.lightboxTarget.replaceChildren(node)
    if (typeof this.lightboxTarget.showModal === 'function') {
      this.lightboxTarget.showModal()
    }
  }

  onLightboxClick = (event) => {
    if (event.target === this.lightboxTarget) this.closeLightbox()
  }

  onLightboxClosed = () => {
    if (this.hasLightboxTarget) this.lightboxTarget.replaceChildren()
  }

  closeLightbox = () => {
    if (!this.hasLightboxTarget || !this.lightboxTarget.open) return
    this.lightboxTarget.close()
  }

  onKeydown = (event) => {
    if (event.key !== 'Escape') return
    if (this.hasLightboxTarget && this.lightboxTarget.open) return
    this.jumpBack()
  }

  onAnchorClick = (event) => {
    const link = event.target.closest('a[href^="#"]')
    if (!link || !this.element.contains(link) || link.closest('.lexiqamus-page-toc, .lexiqamus-lq-backtop')) return

    const id = decodeURIComponent(link.getAttribute('href').slice(1))
    if (!id) return
    const target = document.getElementById(id)
    if (!target) return

    this.detachJump()
    this.jumpFrom = window.scrollY

    const head = target.querySelector('h2,h3,h4') ||
      (/^H[234]$/.test(target.tagName || '') ? target : (target.closest(sel('article')) || target))
    this.jumpHost = head
    this.jumpHost.classList.add(`${P}-jump-host`, `${P}-jump-target`)
    void this.jumpHost.offsetWidth
    this.jumpHost.appendChild(this.jumpBackTarget)
    this.jumpBackTarget.hidden = false
    requestAnimationFrame(() => this.jumpBackTarget.classList.add(`${P}-jump-back--visible`))
    this.autoHideJump()
    if (this.jumpTargetTimer) clearTimeout(this.jumpTargetTimer)
    this.jumpTargetTimer = setTimeout(() => {
      this.jumpHost?.classList.remove(`${P}-jump-target`)
      this.jumpTargetTimer = null
    }, 2400)
  }

  onDismissJump = (event) => {
    if (event?.target?.closest?.(sel('jump-back'))) return
    if (!this.jumpBackTarget.hidden) {
      this.jumpFrom = null
      this.detachJump()
    }
  }

  jumpBack () {
    if (this.jumpFrom == null) return
    window.scrollTo({ top: this.jumpFrom, behavior: 'smooth' })
    this.jumpFrom = null
    this.detachJump()
  }

  autoHideJump () {
    if (this.jumpTimer) clearTimeout(this.jumpTimer)
    this.jumpTimer = setTimeout(() => {
      this.jumpFrom = null
      this.detachJump()
    }, 5000)
  }

  detachJump () {
    if (this.hasJumpBackTarget) {
      this.jumpBackTarget.classList.remove(`${P}-jump-back--visible`)
      this.jumpBackTarget.hidden = true
      if (this.jumpBackTarget.parentElement !== this.element) this.element.appendChild(this.jumpBackTarget)
    }
    if (this.jumpHost) this.jumpHost.classList.remove(`${P}-jump-host`, `${P}-jump-target`)
    if (this.jumpTimer) {
      clearTimeout(this.jumpTimer)
      this.jumpTimer = null
    }
    if (this.jumpTargetTimer) {
      clearTimeout(this.jumpTargetTimer)
      this.jumpTargetTimer = null
    }
  }
}
